import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslateService } from '@ngx-translate/core';

import { ButtonComponent } from '@shared/components/button';
import { DIALOG_DATA, DialogRef } from '@shared/modules/dialog';

import { appClose40Icon } from '@icons/close-40';
import { appWarning16Icon } from '@icons/warning-16';

import { ConfirmationDialogViewModel } from './confirmation-dialog.view-model';

@Component({
  selector: 'app-confirmation-dialog',
  imports: [SvgIconComponent, AsyncPipe, ButtonComponent, NgClass],
  templateUrl: './confirmation-dialog.component.html',
  styleUrl: './confirmation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialogComponent {
  readonly #dialogRef = inject(DialogRef, {
    optional: true,
  });
  readonly #translateService = inject(TranslateService);
  public readonly dataThroughToken = inject<ConfirmationDialogViewModel>(DIALOG_DATA, {
    optional: true,
  });

  public readonly data = input<ConfirmationDialogViewModel>();
  public readonly dismiss = output<boolean>();

  public readonly vm = computed(() => {
    const data = this.data();
    const dataThroughToken = this.dataThroughToken;

    if (data) {
      return data;
    }

    if (dataThroughToken) {
      return dataThroughToken;
    }

    throw new Error('ConfirmationDialogComponent: No data provided');
  });

  public readonly defaultConfirmButtonLabel$ = this.#translateService.stream('shared.confirmation-dialog.confirm');
  public readonly defaultCancelButtonLabel$ = this.#translateService.stream('shared.confirmation-dialog.cancel');

  constructor() {
    injectRegisterIcons([appClose40Icon, appWarning16Icon]);
  }

  public onCancelClick(): void {
    this.dismiss.emit(false);

    if (!this.data()) {
      this.#dialogRef?.dismiss(false);
    }
  }

  public onConfirmClick(): void {
    this.dismiss.emit(true);

    if (!this.data()) {
      this.#dialogRef?.dismiss(true);
    }
  }
}
